import { environment } from '../../environments/environment';
import { EStoreTypes, EUserRoles } from '../configurations/common';
import { INavigationNodes, IStore } from '../models/settings.model';
import { LocalStorageUtils } from './localStorage.utils';
import { ISelectEvent } from '../models/common.models';
import { UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';

export class AppUtils {

  static readonly jwtGidElementName: string = 'https://login-shs.siemens-healthineers.com/gid';
  static readonly mobileDeviceMaxWidth: string = '62em'; // this width is for mobile devices and tablets corresponding to 992px

  static getStoreIdFromLocalStorage(): string {
    return LocalStorageUtils.getKeyValue('storeId');
  }

  static getCurrentStore(): IStore {
    const currentStoreId: string = this.getStoreIdFromLocalStorage();
    const defaultStore: IStore = this.getDefaultStore();
    if (!currentStoreId) {
      return defaultStore;
    }
    return environment.stores.find(store => store.storeId === currentStoreId) as IStore;
  }

  /**
   * Checks if the provided storeId exists in the environment stores.
   * Changes the storeId to uppercase before checking.
   * @param {string} storeId
   * @returns {boolean}
   */
  static checkIfStoreIdExists(storeId: string | null | undefined): boolean {
    if (!storeId) {
      return false;
    }
    return environment.stores.some(store => store.storeId === storeId.toUpperCase());
  }

  static getCurrentMarket(): string {
    const currentStore: IStore = this.getCurrentStore();
    return this.getMarketByStoreId(currentStore.storeId);
  }

  static getDefaultStore(): IStore {
    const defaultStore: string = environment.defaultStore;
    return environment.stores.find(store => store.storeId === defaultStore)!;
  }

  static getMarketByStoreId(storeId: string): string {
    return environment.stores.find(store => store.storeId === storeId)!.marketCode;
  }

  static isStoreActive(store: EStoreTypes): boolean {
    return AppUtils.getCurrentStore().storeId === store;
  }

  static isSapStore(): boolean {
    switch (AppUtils.getCurrentStore().storeId) {
      case 'US':
      case 'CA':
      case 'MEA':
        return true;
      default:
        return false;
    }
  }

  static filterNodesByRole(navigationNodes: INavigationNodes[], companyRoles: EUserRoles[]): INavigationNodes[] {
    return navigationNodes.filter(node =>
      !node.whitelistedRoles || node.whitelistedRoles?.some(role => companyRoles.includes(role)),
    );
  }

  static isProductionEnv(): boolean {
    return environment.production;
  }

  static getEnvName(): string {
    return environment.name;
  }

  /**
   * Method to check if the device is mobile based on the provided maxWidth
   * @param {BreakpointObserver} breakpointObserver
   * @param {string} maxWidth
   * @returns {Observable<boolean>}
   */
  static isMobileDevice(breakpointObserver: BreakpointObserver, maxWidth: string): Observable<boolean> {
    const customBreakpoint: string = `(max-width: ${maxWidth})`;
    return breakpointObserver.observe([customBreakpoint]).pipe(
      map(result => result.matches),
    );
  }

  /**
   * Method for setting the form value
   * @param {ISelectEvent} event
   * @param {UntypedFormGroup} form
   */
  static setFormValue(event: ISelectEvent, form: UntypedFormGroup): void {
    form.patchValue({
      [event.key]: event.value,
    });
  }
}
